"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MassachusettsRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const unwrap_1 = require("../../unwrap");
const onSiteAccumulationThresholds_1 = require("../onSiteAccumulationThresholds");
const undeterminedStricterOfClock_1 = require("../undeterminedStricterOfClock");
const FEDERAL_1 = require("./FEDERAL");
/**
 * Massachusetts (MassDEP) accumulation limits, 310 CMR 30.000.
 *
 * Massachusetts runs its own generator tiers alongside the federal ones, so
 * the clocks key on the stricter of the two axes — the
 * `effectiveGeneratorStatus` derivation below.
 *
 * ── KNOWN GAPS
 *
 * 1. WASTE OIL generator status - see effectiveWasteKind description.
 * 2. VSQG acute ban - see MA_VSQG_THRESHOLD_NOTE.
 * 3. Regulated recyclable material - see MA_SQG_THRESHOLD_NOTE.
 * 4. Acute mass cap, VSQG per-stream allowance, full containers - see MA_SATELLITE_NOTE.
 */
const MA_STATE_STATUS_DOMAIN = [
    "LARGE_QUANTITY_GENERATOR",
    "SMALL_QUANTITY_GENERATOR",
    "VERY_SMALL_QUANTITY_GENERATOR",
    "SAME_AS_FEDERAL",
];
const MA_DERIVATIONS = {
    effectiveGeneratorStatus: {
        from: ["generatorStatus", "stateGeneratorStatus"],
        label: "Effective Massachusetts tier",
        citation: "310 CMR 30.340, 30.351, 30.353",
        description: (0, unwrap_1.unwrap) `
      A facility's Massachusetts generator tier is the stricter of the federal
      generator status and the Massachusetts status.
      LQG on either -> LQG. That is the strictest possible, so it does not matter whether the
      other axis is known. Otherwise, if either axis is unknown we cannot rule out LQG, so the
      honest answer is unknown.

      "Same as Federal" is an artifact of RCRAInfo, but must be represented explicitly due to
      internal architecture constraints.
    `,
        cases: [
            // LQG on either axis wins outright, even if the other is unknown.
            { when: { stateGeneratorStatus: "LARGE_QUANTITY_GENERATOR" }, produces: "LQG" },
            {
                when: { generatorStatus: "LQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: "LQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: "LQG", stateGeneratorStatus: null },
                produces: "LQG",
            },
            // Both axes known, neither LQG: the stricter tier wins.
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: "VSQG",
            },
            // Insufficient information
            { when: { generatorStatus: "SQG", stateGeneratorStatus: null }, produces: null },
            { when: { generatorStatus: "VSQG", stateGeneratorStatus: null }, produces: null },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            {
                when: { generatorStatus: null, stateGeneratorStatus: "VERY_SMALL_QUANTITY_GENERATOR" },
                produces: null,
            },
            { when: { generatorStatus: null, stateGeneratorStatus: null }, produces: null },
            // "Same as Federal"
            {
                when: { generatorStatus: null, stateGeneratorStatus: "SAME_AS_FEDERAL" },
                produces: null,
            },
            {
                when: { generatorStatus: "LQG", stateGeneratorStatus: "SAME_AS_FEDERAL" },
                produces: "LQG",
            },
            {
                when: { generatorStatus: "SQG", stateGeneratorStatus: "SAME_AS_FEDERAL" },
                produces: "SQG",
            },
            {
                when: { generatorStatus: "VSQG", stateGeneratorStatus: "SAME_AS_FEDERAL" },
                produces: "VSQG",
            },
        ],
    },
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste"],
        label: "Massachusetts Effective Subclassification",
        citation: "310 CMR 30.1000, 30.253",
        description: (0, unwrap_1.unwrap) `
      UNIVERSAL_WASTE must be both categorized as such and managed as such; those
      determinations are made elsewhere.

      USED_OIL (KNOWN GAP) is handled in a special way by MA - it is not under federal 40 CFR 279.
      MA's custom handling is not fully supported.
      In MA, you have a used oil generator status whose tiers mirror the haz waste tiers
      (VSQG, SQG, LQG) but WHICH tier you have for each status is completely independent.
      310 CMR 30.253 tells you to determine your used oil generator status first, but then
      read 30.340(6) and 30.341 (LQG), 30.351 (SQG), and 30.353 (VSQG) AS IF they were talking
      about used oil instead of haz waste.
      We ASSUME that your haz waste generator status and your used oil generator status
      are the same. This means that if a facility is a haz waste SQG but a Used Oil LQG,
      containers of Used Oil should get LQG limits, but will get SQG limits instead.

      Pharma is ordinary waste; MA has not adopted 40 CFR 266 subpart P.
    `,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "ORDINARY",
            },
            { when: { wasteKind: "USED_OIL" }, produces: "ORDINARY" },
            { when: { wasteKind: "PHARMACEUTICAL" }, produces: "ORDINARY" },
            { when: { wasteKind: null }, produces: "ORDINARY" },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
const MA_SATELLITE_NOTE = (0, unwrap_1.unwrap) `
  (1) 30.340(6)(c)(2) caps acute waste by VOLUME only; the 1 kg solid-acute figure carried
  here comes from federal 262.15(a)(1) and is stricter than the MA text; MA has pre-GIR pattern.

  (2) The caps reach every tier, with no VSQG carve-out: 310 CMR 30.353(6)(i) gives a very
  small quantity generator its own satellite allowance (one 55-gallon container per waste
  stream, three days once full), where federal 262.15 reaches only LQGs and SQGs. A
  Massachusetts VSQG may hold no acute waste at all, so applying the acute caps to one is
  over-strict and never under-strict.

  (3) 30.353(6)(i)'s limit is per WASTE STREAM, while we judge per accumulation area —
  over-strict for an area holding several streams, never under-strict. One container per
  stream is not enforced.

  (4) Nothing starts 30.353(6)(i)'s three-day window when a container becomes full.
`;
const MA_UNIVERSAL_WASTE_NOTE = (0, unwrap_1.unwrap) `
  310 CMR 30.1000 adopts federal UW standards (40 CFR 273), all five categories.
  Federal citations are shown.
`;
const MA_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION — 310 CMR 30.340, 30.351, 30.353 ─────────────
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "LQG" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "310 CMR 30.340(4)", jurisdiction: "MA" },
        },
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "SQG" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "310 CMR 30.351(5)", jurisdiction: "MA" },
        },
        note: `No extension for waste that must be transported 200+ miles.`,
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: "VSQG" },
        when: { areaType: "CENTRAL" },
        expect: { clock: { kind: "NO_LIMIT", citation: "310 CMR 30.353", jurisdiction: "MA" } },
    },
    {
        given: { effectiveWasteKind: "ORDINARY", effectiveGeneratorStatus: null },
        when: { areaType: "CENTRAL" },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — 310 CMR 30.340(6), 30.353(6)(i) ──────────
    {
        given: { effectiveWasteKind: "ORDINARY" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: {
                kind: "VOLUME",
                nonAcuteGal: 55,
                acuteGal: 0.25,
                acuteKg: 1,
                moveWindowDays: 3,
                citation: "310 CMR 30.340(6), 30.353(6)(i)",
                jurisdiction: "MA",
            },
            clock: { kind: "NO_LIMIT", citation: "310 CMR 30.340(6)", jurisdiction: "MA" },
        },
        note: MA_SATELLITE_NOTE,
    },
    // ── UNIVERSAL WASTE — adopted by reference, 310 CMR 30.1000 ───────────
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: {
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: MA_UNIVERSAL_WASTE_NOTE,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: {
                kind: "TIME",
                days: 365,
                citation: "40 CFR 273.15(a), 273.35(a)",
                jurisdiction: "FEDERAL",
            },
        },
        note: MA_UNIVERSAL_WASTE_NOTE,
    },
];
/**
 * "Same as Federal" is the Massachusetts record deferring to the federal
 * axis, so it resolves to the Massachusetts tier that federal status names
 * before the stricter-of comparison runs.
 */
const MA_TIER_BY_FEDERAL_STATUS = {
    LQG: "LARGE_QUANTITY_GENERATOR",
    SQG: "SMALL_QUANTITY_GENERATOR",
    VSQG: "VERY_SMALL_QUANTITY_GENERATOR",
};
function massachusettsTier(ctx) {
    if (ctx.stateGeneratorStatus === "SAME_AS_FEDERAL") {
        return ctx.generatorStatus === null ? null : MA_TIER_BY_FEDERAL_STATUS[ctx.generatorStatus];
    }
    return ctx.stateGeneratorStatus;
}
/**
 * The imperative twin of the `effectiveGeneratorStatus` derivation above.
 * Sharing `MaDeterminations` keeps the two from drifting on shape; the sweep
 * holds their contents to each other.
 */
function effectiveMassachusettsStatus(ctx) {
    const { generatorStatus } = ctx;
    const stateTier = massachusettsTier(ctx);
    if (generatorStatus === "LQG" || stateTier === "LARGE_QUANTITY_GENERATOR") {
        return "LQG";
    }
    if (generatorStatus === null || stateTier === null) {
        return null;
    }
    if (generatorStatus === "SQG" || stateTier === "SMALL_QUANTITY_GENERATOR") {
        return "SQG";
    }
    if (generatorStatus === "VSQG" || stateTier === "VERY_SMALL_QUANTITY_GENERATOR") {
        return "VSQG";
    }
    // Exhaustiveness guard: only accepts `never`, so an unhandled status
    // combination fails to compile.
    return ((..._) => null)(generatorStatus, stateTier);
}
function effectiveMaWasteKind(ctx) {
    return ctx.wasteKind === "UNIVERSAL_WASTE" && ctx.managedAsUniversalWaste
        ? "UNIVERSAL_WASTE"
        : "ORDINARY";
}
const MA_SQG_THRESHOLD_NOTE = (0, unwrap_1.unwrap) `
  310 CMR 30.351(1)(b) holds a small quantity generator to 6,000 kg of hazardous waste,
  regulated recyclable material, or the two combined - "regulated recyclable material"
  is not currently supported in the system anywhere.
`;
const MA_VSQG_THRESHOLD_NOTE = (0, unwrap_1.unwrap) `
  310 CMR 30.353(1)(b) holds a very small quantity generator to 1,000 kg accumulated at any one
  time, matching federal 262.14(a)(3). The 1 kg acute figure beside it is the FEDERAL number,
  and Massachusetts is stricter: 30.353(1)(c) forbids a VSQG to generate or accumulate any
  acutely hazardous waste at all. That condition is an eligibility gate on the status rather
  than a limit the status implies, so it belongs to the generator status calculation upstream,
  which does not yet apply it. Carried here as the federal number pending that work.`;
/**
 * Spec, not snapshot. Keyed on the effective Massachusetts generator status —
 * the same derivation the clocks use — so each threshold is stated once.
 */
const MA_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        given: { effectiveGeneratorStatus: "LQG" },
        when: {},
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
    },
    {
        given: { effectiveGeneratorStatus: "SQG" },
        when: {},
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
        note: MA_SQG_THRESHOLD_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: "VSQG" },
        when: {},
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
        note: MA_VSQG_THRESHOLD_NOTE,
    },
    {
        given: { effectiveGeneratorStatus: null },
        when: {},
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
    },
];
/** Massachusetts (MassDEP) accumulation limits, 310 CMR 30.000. */
class MassachusettsRules extends FEDERAL_1.StateRules {
    constructor() {
        super(...arguments);
        this.expectations = MA_EXPECTATIONS;
        this.accumulationThresholdExpectations = MA_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = MA_DERIVATIONS;
        this.stateStatusDomain = MA_STATE_STATUS_DOMAIN;
    }
    centralClock(ctx) {
        // Universal waste in MA is governed by 310 CMR 30.1000, which adopts
        // federal 40 CFR 273
        if (effectiveMaWasteKind(ctx) === "UNIVERSAL_WASTE") {
            return super.centralClock(ctx);
        }
        const effective = effectiveMassachusettsStatus(ctx);
        if (effective === "LQG") {
            return { kind: "TIME", days: 90, citation: "310 CMR 30.340(4)", jurisdiction: "MA" };
        }
        if (effective === "SQG") {
            // 180 days flat: Massachusetts adopted no counterpart to federal
            // 262.16(c)'s 270-day extension, so mustTransport200Miles is not read.
            return { kind: "TIME", days: 180, citation: "310 CMR 30.351(5)", jurisdiction: "MA" };
        }
        if (effective === "VSQG") {
            // No time limit — 30.353 constrains a VSQG by quantity instead, which
            // the facility-wide threshold slot below owns.
            return { kind: "NO_LIMIT", citation: "310 CMR 30.353", jurisdiction: "MA" };
        }
        return (0, undeterminedStricterOfClock_1.undeterminedStricterOfClock)(ctx, "Massachusetts");
    }
    satelliteCaps(ctx) {
        if (effectiveMaWasteKind(ctx) === "UNIVERSAL_WASTE") {
            return super.satelliteCaps(ctx);
        }
        // Federal figures under Massachusetts' own regulation, with no VSQG
        // carve-out: 30.353(6)(i) gives a VSQG its own satellite allowance where
        // federal 262.15 reaches only LQGs and SQGs. The acute mass figure is the
        // federal stand-in for 30.340(6)(c)(2)'s quart-only limit — over-strict,
        // never under-strict.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: wasteAccumulation_1.SATELLITE_MOVE_WINDOW_DAYS,
            citation: "310 CMR 30.340(6), 30.353(6)(i)",
            jurisdiction: "MA",
        };
    }
    satelliteClock(ctx) {
        if (effectiveMaWasteKind(ctx) === "UNIVERSAL_WASTE") {
            return super.satelliteClock(ctx);
        }
        // 30.340(6) sets no standing clock on satellite accumulation — the only
        // time limit is the breach-triggered three-day window, carried on the caps.
        return { kind: "NO_LIMIT", citation: "310 CMR 30.340(6)", jurisdiction: "MA" };
    }
    accumulationThresholds(ctx) {
        const effective = effectiveMassachusettsStatus(ctx);
        return {
            // Massachusetts' figures are the federal ones (30.351(1)(b),
            // 30.353(1)(b)), keyed on the EFFECTIVE Massachusetts tier.
            thresholds: (0, onSiteAccumulationThresholds_1.getFederalAccumulationThresholds)(effective),
            judgedAgainst: effective === null ? null : FEDERAL_1.FEDERAL_READABLE_GENERATOR_STATUSES[effective],
        };
    }
}
exports.MassachusettsRules = MassachusettsRules;
//# sourceMappingURL=MA.js.map